<template>
  <ul class="feed" :class="{ compact }">
    <!-- Collapsed by default: steps plus only the latest update; history on demand. -->
    <li v-if="olderCount" class="feed-item feed-history">
      <button type="button" class="feed-toggle" :class="{ open: showAll }" @click="showAll = !showAll">
        {{ showAll ? 'Show only the latest' : `${olderCount} earlier update${olderCount === 1 ? '' : 's'}` }}
      </button>
    </li>
    <li
      v-for="row in visibleRows"
      :key="row.key"
      class="feed-item"
      :class="row.type === 'drafts' ? 'kind-drafts' : ['kind-' + row.item.kind, 'st-' + (row.item.status || row.item.outcome || 'none')]">
      <!-- Suggestions the model has written so far, as chips -->
      <div v-if="row.type === 'drafts'" class="feed-line feed-drafts">
        <span class="feed-icon icon-draft" aria-hidden="true"></span>
        <span class="feed-muted">Drafted</span>
        <span v-for="draft in row.items" :key="draft.id" class="feed-chip">{{ draft.label }}</span>
      </div>

      <template v-else-if="row.item.kind === 'step'">
        <div class="feed-line">
          <span class="feed-icon" :class="stepIcon(row.item)" aria-hidden="true"></span>
          <strong class="feed-label">{{ row.item.label }}</strong>
          <span v-if="row.item.detail" class="feed-detail">{{ row.item.detail }}</span>
          <span v-if="elapsed(row.item)" class="feed-time">{{ elapsed(row.item) }}</span>
          <button
            v-if="hasChildren(row.item) && row.item.status !== 'running'"
            type="button"
            class="feed-toggle"
            @click="toggle(row.item)">{{ isOpen(row.item) ? 'Hide' : 'Details' }}</button>
        </div>
        <job-activity-feed
          v-if="hasChildren(row.item) && isOpen(row.item)"
          class="feed-children"
          :items="items"
          :root="row.item.id"
          :compact="compact" />
      </template>

      <div v-else-if="row.item.kind === 'search'" class="feed-line">
        <span class="feed-icon" :class="row.item.status === 'searching' ? 'icon-spin' : row.item.status === 'failed' ? 'icon-fail' : 'icon-search'" aria-hidden="true"></span>
        <span class="feed-muted">{{ row.item.status === 'searching' && !row.item.query ? 'Searching the web…' : 'Searched' }}</span>
        <span v-if="row.item.query" class="feed-query">{{ row.item.query }}</span>
      </div>

      <!-- Latest update only: the newest paragraph of the model's thinking, two lines. -->
      <p v-else-if="row.item.kind === 'thinking' && row.latest" class="feed-thinking-latest">
        <strong v-if="latestThought(row.item.text).heading">{{ latestThought(row.item.text).heading }}</strong>
        {{ latestThought(row.item.text).text }}
      </p>

      <div v-else-if="row.item.kind === 'thinking'" ref="thinking" class="feed-thinking" :class="{ open: thinkingOpen[row.item.id] }">
        <p v-for="(block, i) in thinkingBlocks(row.item.text)" :key="i" :class="{ heading: block.heading }">{{ block.text }}</p>
        <button
          v-if="!running && (row.item.text || '').length > 280"
          type="button"
          class="feed-toggle"
          @click="$set(thinkingOpen, row.item.id, !thinkingOpen[row.item.id])">{{ thinkingOpen[row.item.id] ? 'Less' : 'All of it' }}</button>
      </div>

      <div v-else-if="row.item.kind === 'result'" class="feed-line">
        <span class="feed-icon" :class="outcomeIcon(row.item.outcome)" aria-hidden="true"></span>
        <span class="feed-label-plain">{{ row.item.label }}</span>
        <span v-if="row.item.detail" class="feed-detail">{{ row.item.detail }}</span>
      </div>

      <div v-else-if="row.item.kind === 'note'" class="feed-line feed-note">{{ row.item.text }}</div>
    </li>
  </ul>
</template>

<script>
import { childrenOf } from '../../../services/jobActivity'

/**
 * Renders a live job activity feed (see services/jobActivity.js): steps with
 * their timing, and under each step what happened inside it — web searches, the
 * model's streamed thinking, drafted results, per-item outcomes. Recursive, so
 * a step inside a step (a search round inside a request) nests naturally.
 */
export default {
  name: 'JobActivityFeed',
  props: {
    items: { type: Array, default: () => [] },
    // Render the children of this item id; null renders the top level.
    root: { type: String, default: null },
    // Tighter type, and finished steps start collapsed (used inside list rows).
    compact: { type: Boolean, default: false }
  },
  data () {
    return {
      now: Date.now(),
      ticker: null,
      openState: {},
      thinkingOpen: {},
      // History of updates under a step is collapsed to the latest one.
      showAll: false
    }
  },
  computed: {
    ownItems () {
      return childrenOf(this.items, this.root)
    },
    /** Consecutive drafts collapse into one row of chips. */
    rows () {
      const rows = []
      this.ownItems.forEach((item) => {
        const last = rows[rows.length - 1]
        if (item.kind === 'draft') {
          if (last && last.type === 'drafts') last.items.push(item)
          else rows.push({ key: 'drafts-' + item.id, type: 'drafts', items: [item] })
          return
        }
        rows.push({ key: item.id, type: 'item', item })
      })
      return rows
    },
    /** Updates (anything that is not a step) older than the newest one. */
    olderCount () {
      return Math.max(this.rows.filter((row) => !this.isStepRow(row)).length - 1, 0)
    },
    /**
     * Steps always show — they are the outline of what is happening. Of the
     * updates inside (searches, thinking, drafts, checks) only the newest shows
     * until the history is opened.
     */
    visibleRows () {
      if (this.showAll || !this.olderCount) return this.rows
      const updates = this.rows.filter((row) => !this.isStepRow(row))
      const latest = updates[updates.length - 1]
      return this.rows
        .filter((row) => this.isStepRow(row) || row === latest)
        .map((row) => (row === latest ? Object.assign({}, row, { latest: true }) : row))
    },
    running () {
      return this.ownItems.some((item) => item.kind === 'step' && item.status === 'running')
    },
    thinkingText () {
      return this.ownItems.filter((item) => item.kind === 'thinking').map((item) => item.text || '').join('|')
    }
  },
  watch: {
    running: {
      immediate: true,
      handler (running) {
        if (running && !this.ticker) this.ticker = setInterval(() => { this.now = Date.now() }, 1000)
        if (!running && this.ticker) { clearInterval(this.ticker); this.ticker = null }
      }
    },
    // Keep the newest thinking in view while it streams.
    thinkingText () {
      this.$nextTick(() => {
        const boxes = [].concat(this.$refs.thinking || [])
        boxes.forEach((box) => { if (box && !box.classList.contains('open')) box.scrollTop = box.scrollHeight })
      })
    }
  },
  beforeDestroy () {
    if (this.ticker) clearInterval(this.ticker)
  },
  methods: {
    isStepRow (row) {
      return row.type === 'item' && row.item.kind === 'step'
    },
    /** The newest paragraph of streamed thinking, with the heading it sits under. */
    latestThought (text) {
      const blocks = this.thinkingBlocks(text)
      let heading = ''
      let body = ''
      blocks.forEach((block) => {
        if (block.heading) { heading = block.text; body = '' } else body = block.text
      })
      return { heading, text: body }
    },
    hasChildren (item) {
      return this.items.some((child) => child.parent === item.id)
    },
    isOpen (item) {
      if (Object.prototype.hasOwnProperty.call(this.openState, item.id)) return this.openState[item.id]
      // Running steps are always open; finished ones stay open unless compact.
      return item.status === 'running' || !this.compact
    },
    toggle (item) {
      this.$set(this.openState, item.id, !this.isOpen(item))
    },
    stepIcon (item) {
      return {
        running: 'icon-spin',
        done: 'icon-done',
        failed: 'icon-fail',
        waiting: 'icon-wait'
      }[item.status] || 'icon-wait'
    },
    outcomeIcon (outcome) {
      return {
        checking: 'icon-spin',
        available: 'icon-done',
        filed: 'icon-done',
        held: 'icon-held',
        skipped: 'icon-skip',
        rejected: 'icon-fail'
      }[outcome] || 'icon-skip'
    },
    elapsed (item) {
      let ms = null
      if (item.status === 'running' && item.startedAt) ms = this.now - new Date(item.startedAt).getTime()
      else if (item.ms != null) ms = Number(item.ms)
      if (ms == null || !Number.isFinite(ms) || ms < 0) return ''
      const seconds = Math.round(ms / 1000)
      if (seconds < 60) return `${seconds}s`
      return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`
    },
    /** Reasoning summaries arrive as markdown-ish paragraphs with **headings**. */
    thinkingBlocks (text) {
      return String(text || '')
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block) => {
          const heading = /^\*\*[^*]+\*\*$/.test(block)
          return { heading, text: block.replace(/\*\*/g, '') }
        })
    }
  }
}
</script>

<style scoped>
.feed { display: grid; gap: 5px; margin: 0; padding: 0; list-style: none; font-size: 8.6pt; }
.feed.compact { gap: 3px; font-size: 8.1pt; }
.feed-children { margin: 5px 0 2px 7px; padding-left: 12px; border-left: 1px solid rgba(67,72,87,0.7); }
.feed-line { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; min-width: 0; line-height: 1.45; }
.feed-label { color: #dfe4ee; font-weight: 700; }
.feed-label-plain { color: #cfd2da; }
.feed-detail { color: #8f98aa; }
.feed-muted { color: #7f889a; }
.feed-query { color: #9bdff5; }
.feed-time { margin-left: auto; color: #6f7788; font-size: 7.8pt; font-variant-numeric: tabular-nums; }
.feed-note { color: #e0a856; font-style: italic; }
.feed-chip {
  max-width: 260px;
  overflow: hidden;
  padding: 0 7px;
  border: 1px solid rgba(196,167,245,0.35);
  border-radius: 999px;
  color: #d6c4f8;
  font-size: 7.8pt;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.feed-toggle {
  padding: 0;
  border: 0;
  background: none;
  color: #7fd0ee;
  font: inherit;
  font-size: 7.8pt;
  cursor: pointer;
}
.feed-toggle:hover { color: #eef8fc; }
.feed-toggle:focus { outline: none; }
.feed-toggle:focus-visible { outline: 1px solid rgba(25,151,198,0.8); outline-offset: 2px; }
.feed-thinking {
  max-height: 7.4em;
  overflow-y: auto;
  padding: 6px 9px;
  border-left: 2px solid rgba(196,167,245,0.45);
  border-radius: 0 6px 6px 0;
  background: rgba(196,167,245,0.05);
  color: #aeb6c6;
  font-style: italic;
  line-height: 1.45;
}
.feed-thinking.open { max-height: none; }
.feed-thinking-latest {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  padding-left: 9px;
  border-left: 2px solid rgba(196,167,245,0.45);
  color: #aeb6c6;
  font-style: italic;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.feed-thinking-latest strong { margin-right: 4px; color: #d6c4f8; font-style: normal; }
.feed-history { line-height: 1.2; }
.feed-history .feed-toggle::before { content: '▸ '; }
.feed-history .feed-toggle.open::before { content: '▾ '; }
.feed-thinking p { margin: 0 0 4px; }
.feed-thinking p:last-of-type { margin-bottom: 0; }
.feed-thinking p.heading { color: #d6c4f8; font-style: normal; font-weight: 700; }
.feed-icon {
  position: relative;
  top: 1px;
  display: inline-block;
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.icon-spin { border: 2px solid rgba(25,151,198,0.3); border-top-color: #16b4ed; animation: feed-spin 0.8s linear infinite; }
.icon-done { background: #1bc98e; box-shadow: 0 0 0 2px rgba(27,201,142,0.18); }
.icon-fail { background: #f07d92; box-shadow: 0 0 0 2px rgba(240,125,146,0.18); }
.icon-held { background: #e0a856; box-shadow: 0 0 0 2px rgba(224,168,86,0.18); }
.icon-skip { background: #5d6475; }
.icon-wait { border: 1px solid #5d6475; }
.icon-search { background: #1997c6; }
.icon-draft { border-radius: 2px; background: #c4a7f5; transform: rotate(45deg) scale(0.75); }
@keyframes feed-spin { to { transform: rotate(360deg); } }
</style>
